import { useCallback, useEffect, useMemo, useState } from 'react'
import { logger } from '@/core/logger'
import { track } from '@/core/analytics'
import {
  detectPlatform,
  installStrategy,
  isStandalone,
  type InstallPlatform,
  type InstallStrategy,
} from '../domain/install-detection'

/**
 * `BeforeInstallPromptEvent` isn't in the standard DOM lib types yet —
 * declared inline so we don't need a global ambient file. Only Chromium
 * browsers fire it.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[]
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const NUDGE_DISMISSED_KEY = 'cm.install.nudgeDismissedAt'

export type InstallStatus = 'installed' | 'installable' | 'manual' | 'unsupported' | 'unknown'

export interface InstallState {
  status: InstallStatus
  platform: InstallPlatform
  strategy: InstallStrategy
  /** True when a `beforeinstallprompt` event has been captured and the
   *  native prompt is ready to fire. */
  canPromptNative: boolean
  /** Fires the captured native prompt (Chromium only). Resolves with the
   *  user's outcome. No-op when no prompt is available. */
  prompt: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
  /** Mark the sidebar nudge as dismissed (persisted in localStorage).
   *  Doesn't affect the Settings → Instalación tab. */
  dismissNudge: () => void
  /** Whether the sidebar nudge should be shown right now. */
  shouldShowNudge: boolean
}

function readIosStandalone(): boolean {
  // Safari-only legacy property. Cast to a record to avoid the global
  // `Navigator` ambient lib not declaring it.
  const nav = navigator as unknown as Record<string, unknown>
  return nav['standalone'] === true
}

function readDisplayModeStandalone(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  try {
    return window.matchMedia('(display-mode: standalone)').matches
  } catch {
    return false
  }
}

/**
 * Reactive install detection. Wraps `beforeinstallprompt`, the
 * standalone-display-mode media query, and platform UA sniffing. Telemetry
 * fires once per session when a definite status is reached so we can see
 * how many clubs are running as a PWA without flooding events on
 * intermediate state.
 */
export function useInstallState(): InstallState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return readDisplayModeStandalone() || readIosStandalone()
  })
  const [nudgeDismissedAt, setNudgeDismissedAt] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.localStorage.getItem(NUDGE_DISMISSED_KEY)
      return raw ? Number(raw) : null
    } catch {
      return null
    }
  })

  // Capture `beforeinstallprompt` (Chromium). preventDefault keeps the
  // native mini-bar from showing — we want the in-app trigger instead.
  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setDeferred(null)
      track('app_installed')
    }
    window.addEventListener('beforeinstallprompt', onPrompt as EventListener)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt as EventListener)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  // React to the user installing the app via the browser UI (the standalone
  // media query flips when this happens in some environments).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const mq = window.matchMedia('(display-mode: standalone)')
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setInstalled(true)
    }
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])

  const platform = useMemo<InstallPlatform>(() => {
    if (typeof navigator === 'undefined') return 'unknown'
    return detectPlatform({
      userAgent: navigator.userAgent,
      standalone: readDisplayModeStandalone(),
      iosStandalone: readIosStandalone(),
    })
  }, [])

  const strategy = useMemo<InstallStrategy>(() => installStrategy(platform), [platform])

  const status = useMemo<InstallStatus>(() => {
    if (typeof navigator === 'undefined') return 'unknown'
    if (
      installed ||
      isStandalone({
        userAgent: navigator.userAgent,
        standalone: readDisplayModeStandalone(),
        iosStandalone: readIosStandalone(),
      })
    ) {
      return 'installed'
    }
    if (deferred) return 'installable'
    if (strategy === 'manual') return 'manual'
    if (strategy === 'native') {
      // Chromium without a deferred prompt — usually means the install
      // criteria haven't fired yet (no PWA criteria met, or the user
      // already dismissed twice). Treat as installable-pending so we
      // show a friendly "Cómo instalar" link instead of native button.
      return 'manual'
    }
    return 'unsupported'
  }, [installed, deferred, strategy])

  // One-shot telemetry: fire once when a settled status is known.
  useEffect(() => {
    if (status === 'unknown') return
    track('app_install_state', { status, platform })
    // Only first time per mount — re-tracking on every change would
    // skew the funnel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const prompt = useCallback(async (): Promise<
    'accepted' | 'dismissed' | 'unavailable'
  > => {
    if (!deferred) return 'unavailable'
    try {
      await deferred.prompt()
      const choice = await deferred.userChoice
      track('app_install_prompt_result', { outcome: choice.outcome })
      // The event can only be used once. Drop the reference so the UI
      // re-renders to "instalada" / "no disponible".
      setDeferred(null)
      return choice.outcome
    } catch (err) {
      logger.e('install prompt failed', err)
      setDeferred(null)
      return 'dismissed'
    }
  }, [deferred])

  const dismissNudge = useCallback(() => {
    const now = Date.now()
    setNudgeDismissedAt(now)
    try {
      window.localStorage.setItem(NUDGE_DISMISSED_KEY, String(now))
    } catch {
      // localStorage may be unavailable (private mode) — silent.
    }
    track('app_install_nudge_dismissed', { platform })
  }, [platform])

  // Nudge is shown when the app is installable AND the user hasn't
  // dismissed it. Once installed, we never show it again. For `manual`
  // strategies we don't show a nudge (the wizard is enough).
  const shouldShowNudge =
    status === 'installable' && nudgeDismissedAt == null

  return {
    status,
    platform,
    strategy,
    canPromptNative: deferred != null,
    prompt,
    dismissNudge,
    shouldShowNudge,
  }
}
